import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useWeekStore } from '@/stores/week'

/**
 * Composable to ensure week data (including repObj) is available before view renders.
 * Handles PWA background recovery scenario where store state is lost.
 *
 * Usage:
 *   const { isReady, isRecovering, repObj } = useEnsureWeekData()
 *
 * In template:
 *   <LoadingSpinner v-if="isRecovering" />
 *   <div v-else-if="isReady">...</div>
 */
export function useEnsureWeekData(options = {}) {
    const { redirectOnFail = true } = options

    const router = useRouter()
    const weekStore = useWeekStore()

    const isRecovering = ref(false)
    const recoveryFailed = ref(false)

    const isReady = computed(() => {
        return weekStore.weekData !== null && weekStore.repObj !== null
    })

    const repObj = computed(() => weekStore.repObj)
    const weekData = computed(() => weekStore.weekData)

    const ensureData = async () => {
        // Already have data, nothing to do
        if (isReady.value) {
            return true
        }

        isRecovering.value = true

        try {
            // checkWeekData will restore from localStorage and/or fetch from API
            await weekStore.checkWeekData()

            if (isReady.value) {
                return true
            }

            // Still no data after recovery attempt
            recoveryFailed.value = true

            if (redirectOnFail) {
                console.warn('[useEnsureWeekData] Week data recovery failed, redirecting to week selection')
                router.replace({ name: 'SelectWeek' })
            }

            return false
        } catch (error) {
            console.error('[useEnsureWeekData] Error recovering week data:', error)
            recoveryFailed.value = true

            if (redirectOnFail) {
                router.replace({ name: 'SelectWeek' })
            }

            return false
        } finally {
            isRecovering.value = false
        }
    }

    onMounted(() => {
        if (!isReady.value) {
            ensureData()
        }
    })

    return {
        isReady,
        isRecovering,
        recoveryFailed,
        repObj,
        weekData,
        ensureData
    }
}
